import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaEye, FaEyeSlash, FaLock, FaShieldAlt, FaTimes } from 'react-icons/fa';
import PrivacyPolicyModal from './PrivacyPolicyModal';
import ForcePasswordChangeModal from './ForcePasswordChangeModal';
import AdminAuthService from '../services/AdminAuthService';
import { useTheme } from '../themeConfig';

const AdminLoginModal = ({ isOpen, onClose, onVerify, pageId }) => {
  const { theme } = useTheme();
  const [step, setStep] = useState('password'); // 'password' | 'force_change' | 'privacy'
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tempSession, setTempSession] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await AdminAuthService.verifyAdminPassword(pageId, password);
      if (result.success) {
        if (result.mustChangePassword) {
          setStep('force_change');
        } else {
          setTempSession(result);
          setStep('privacy');
        }
      } else {
        setError('Incorrect password. Try again.');
      }
    } catch (err) {
      if (err.code === 'functions/failed-precondition') {
        setError('This page needs to be initialized. Please run the migration script first.');
      } else if (err.code === 'functions/not-found') {
        setError('Page not found. Please check your NFC/QR link.');
      } else {
        setError(err.message || 'Verification failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChanged = (sessionData) => {
    setTempSession(sessionData);
    setStep('privacy');
  };

  const handlePrivacyAccept = async () => {
    if (tempSession) {
      try {
        setLoading(true);
        await AdminAuthService.establishSession(pageId, tempSession);
        onVerify(true);
        resetAndClose();
      } catch (err) {
        setError('Failed to establish secure session.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePrivacyReject = () => {
    resetAndClose();
  };

  const resetAndClose = () => {
    setStep('password');
    setPassword('');
    setShowPassword(false);
    setError('');
    setTempSession(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <ForcePasswordChangeModal
        isOpen={step === 'force_change'}
        pageId={pageId}
        oldPassword={password}
        onSuccess={handlePasswordChanged}
        onCancel={resetAndClose}
      />

      <PrivacyPolicyModal
        isOpen={step === 'privacy'}
        onAccept={handlePrivacyAccept}
        onReject={handlePrivacyReject}
      />

      <AnimatePresence>
        {isOpen && step === 'password' && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-5 backdrop-blur-xl bg-black/35">
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="lumirae-ds-panel relative z-10 w-full max-w-sm p-6"
              style={{
                '--lumirae-accent-primary': theme.accent,
                '--lumirae-accent-soft': theme.accentSoft,
                '--lumirae-surface-raised': theme.cardBg,
                '--lumirae-border-default': theme.cardBorder,
                '--lumirae-text-primary': theme.text,
                '--lumirae-text-secondary': theme.secondaryText,
              }}
            >
              <button
                type="button"
                onClick={resetAndClose}
                className="lumirae-ds-icon-button absolute right-5 top-5"
                aria-label="Close admin login"
              >
                <FaTimes size={20} />
              </button>

              <div className="flex flex-col items-center text-center space-y-6 pt-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-[1.35rem] text-white shadow-xl" style={{ backgroundColor: theme.accent }}>
                  <FaShieldAlt size={32} />
                </div>

                <div className="space-y-2">
                  <p className="lumirae-ds-meta" style={{ color: theme.accent }}>Secure Gift Studio</p>
                  <h2 className="text-2xl font-black uppercase leading-tight" style={{ color: theme.text }}>
                    Unlock editing
                  </h2>
                  <p className="mx-auto max-w-xs text-xs font-bold leading-relaxed" style={{ color: theme.secondaryText }}>
                    Enter the page password to edit this personal digital gift.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="w-full space-y-4">
                  <div className="relative">
                    <input
                      autoFocus
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="ENTER PASSCODE"
                      className="lumirae-ds-input px-6 py-4 pr-14 text-center text-xl font-black tracking-[0.08em]"
                      style={{ borderColor: error ? theme.stateError : theme.cardBorder }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((value) => !value)}
                      className="absolute right-4 top-1/2 -translate-y-1/2"
                      style={{ color: theme.accent }}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                    </button>
                  </div>

                  {error && (
                    <p className="lumirae-ds-soft-note p-3 text-xs font-black uppercase leading-relaxed tracking-[0.12em]" style={{ color: theme.stateError }}>
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="lumirae-ds-button lumirae-ds-button-primary flex w-full items-center justify-center gap-3 px-5 py-4 disabled:opacity-55"
                  >
                    {loading ? 'Verifying...' : <><FaLock size={14} /> Verify Identity</>}
                  </button>
                </form>

                <p className="lumirae-ds-meta">Unauthorized access is monitored.</p>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AdminLoginModal;

